import Moment from 'moment'

import { buildStandardAppointmentActivities } from '../constants/ActivityTemplates'

const WORD_NUMBERS = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  twelve: 12,
}

/**
 * Clones an existing appointment record into a sanitized draft appointment
 * ready for scheduling, preserving operational metadata (title, provider,
 * address, translation/transportation services, description) while clearing
 * encounter-specific clinical notes, expenses, files, and completion status,
 * and pre-seeding fresh standard activity template rows.
 */
export function buildDuplicatedAppointment(sourceAppt, currentUser) {
  if (!sourceAppt || typeof sourceAppt !== 'object') {
    return {
      caseNumber: undefined,
      assignedToUserId: currentUser?.id || '',
      complete: false,
      title: '',
      provider: '',
      individualProvider: '',
      notes: [],
      activities: buildStandardAppointmentActivities(null, currentUser?.id),
      expenses: [],
      fileIds: [],
      tasks: [],
    }
  }

  const assignedUserId = sourceAppt.assignedToUserId || currentUser?.id || ''

  const duplicated = {
    caseNumber: sourceAppt.caseNumber,
    assignedToUserId: assignedUserId,
    appointmentNumber: undefined,
    complete: false,
    title: sourceAppt.title || '',
    provider: sourceAppt.provider || '',
    individualProvider: sourceAppt.individualProvider || '',
    description: sourceAppt.description || '',
    isAllDay: Boolean(sourceAppt.isAllDay),
    startUtc: null,
    endUtc: null,
    translationServicesNeeded: Boolean(sourceAppt.translationServicesNeeded),
    translationServiceId: sourceAppt.translationServiceId ?? null,
    transportationServicesNeeded: Boolean(sourceAppt.transportationServicesNeeded),
    transportationService: sourceAppt.transportationService || '',
    transportationServiceId: sourceAppt.transportationServiceId ?? null,
    note: '',
    subjectiveNote: '',
    objectiveNote: '',
    assessmentNote: '',
    planNote: '',
    notes: [],
    activities: buildStandardAppointmentActivities(null, assignedUserId),
    expenses: [],
    fileIds: [],
    tasks: [],
  }

  if (sourceAppt.address && typeof sourceAppt.address === 'object') {
    duplicated.address = {
      line1: sourceAppt.address.line1 || '',
      city: sourceAppt.address.city || '',
      state: sourceAppt.address.state || '',
      zip: sourceAppt.address.zip || '',
    }
  }

  return duplicated
}

/**
 * Inspects clinical notes and plan fields on a source appointment for explicit
 * follow-up intervals (e.g., "follow up in 2 weeks", "return in 10 days",
 * "re-evaluate in 4 to 6 weeks", "next visit in 1 month") and calculates a
 * suggested follow-up date relative to the source appointment's date (or today).
 */
export function detectFollowUpSuggestion(sourceAppt, referenceToday = null) {
  if (!sourceAppt || typeof sourceAppt !== 'object') return null

  const noteSegments = [
    sourceAppt.planNote,
    sourceAppt.note,
    sourceAppt.assessmentNote,
    sourceAppt.description,
    ...(Array.isArray(sourceAppt.notes) ? sourceAppt.notes.map(n => (typeof n === 'string' ? n : n?.note)) : []),
  ]
    .filter(Boolean)
    .map(s => String(s).replace(/<[^>]*>/g, ' '))

  const combinedText = noteSegments.join(' \n ')
  if (!combinedText.trim()) return null

  const followUpRegex =
    /\b(?:follow[\s-]*up|f\/u|return(?:\s+to\s+clinic)?|rtc|re-?evaluate|recheck|next\s+(?:visit|appointment)|see\s+(?:patient|pt|him|her)\s+back)\s+(?:in\s+)?(\d+|one|two|three|four|five|six|seven|eight|nine|ten|twelve)(?:\s*(?:-|to)\s*(\d+))?\s*(day|days|week|weeks|wk|wks|month|months|mo|mos)\b/i

  const match = combinedText.match(followUpRegex)
  if (!match) return null

  const rawCount = match[1].toLowerCase()
  const count = WORD_NUMBERS[rawCount] || parseInt(rawCount, 10)
  if (!count || Number.isNaN(count) || count <= 0) return null

  const rawUnit = match[3].toLowerCase()
  let momentUnit = 'days'
  let unitLabel = count === 1 ? 'day' : 'days'
  if (rawUnit.startsWith('w')) {
    momentUnit = 'weeks'
    unitLabel = count === 1 ? 'week' : 'weeks'
  } else if (rawUnit.startsWith('m')) {
    momentUnit = 'months'
    unitLabel = count === 1 ? 'month' : 'months'
  }

  const baseMoment =
    sourceAppt.startUtc && Moment.utc(sourceAppt.startUtc).isValid()
      ? Moment.utc(sourceAppt.startUtc).local()
      : referenceToday
      ? Moment(referenceToday)
      : Moment()

  const suggestedMoment = baseMoment.clone().add(count, momentUnit)
  const anchorLabel =
    sourceAppt.startUtc && Moment.utc(sourceAppt.startUtc).isValid() ? 'from prior visit' : 'from today'

  return {
    suggestedDate: suggestedMoment.format('YYYY-MM-DD'),
    displayDate: suggestedMoment.format('MM/DD/YYYY'),
    label: `${count} ${unitLabel} ${anchorLabel}`,
    matchedText: match[0].trim(),
  }
}
